<template>
    <div v-in-viewport="fetchData">
        <graph-title
            :description="$t('analytics.vacancy_dates.description').toString()"
            :show-tooltip="true"
            icon="table"
            :title="$t('analytics.vacancy_dates.title')"
        />
        <div class="w-full">
            <div>
                <div v-if="fetching" class="h-336 w-full flex items-center justify-center">
                    <loading color="blue" size="small"></loading>
                </div>
                <div v-else>
                    <div>
                        <div class="w-full flex mb-6 justify-end">
                            <button id="downloadChartVacanciesDates"
                                class="rounded-32 flex flex-none items-center justify-center trans-ease direction-column"
                                @click="exportData"
                            >
                                <svg-icon
                                    class="w-24 h-24 block fill-[#6e8192] hover:fill-current flex-none trans-ease"
                                    type="download"></svg-icon>
                            </button>
                        </div>
                        <div class="relative">
                            <ag-grid
                                ref="vacanciesDates"
                                :column-defs="columnDefs"
                                :defaultColDef="defaultColDef"
                                :pagination="true"
                                :paginationPageSize="itemsPerPage"
                                :paginationPageSizeSelector="false"
                                :row-data="rowData"
                                :suppressPaginationPanel="true"
                                class="ag-theme-quartz w-full"
                                domLayout="autoHeight"
                                @grid-ready="onGridReady"
                            ></ag-grid>
                            <pagination
                                v-if="totalPages > 1"
                                :items-per-page="itemsPerPage"
                                :total-items="rowData.length"
                                @next="gridApi.paginationGoToNextPage()"
                                @prev="gridApi.paginationGoToPreviousPage()"
                                @go-to="gridApi.paginationGoToPage($event)"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import * as AnalyticsService from '../../services/AnalyticsService'
import GraphTitle from "./GraphTitle.vue";

import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';

// Register all community features
ModuleRegistry.registerModules([AllCommunityModule]);
import {AgGridVue} from "ag-grid-vue3";

import StatusComponent from "./gridComponents/StatusComponent.vue";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import Loading from "../ui/Loading.vue";
import SvgIcon from '../ui/SvgIcon.vue';
import Moment from 'moment';
import {extendMoment} from 'moment-range'
import {mapState} from "pinia";
import {useMainStore} from '../../state/store';
import Pagination from "../navigation/FlatPagination.vue";
import NewFeatureTag from "../ui/NewFeatureTag.vue";
import analyticsMixins from "../../mixins/analyticsMixins";

const moment = extendMoment(Moment);

export default {
    name: "VacanciesDates",
    components: {
        NewFeatureTag,
        Pagination,
        Loading,
        GraphTitle,
        'ag-grid': AgGridVue,
        'svg-icon': SvgIcon,
        StatusComponent,
    },
    props: {
        filterData: {type: Object},
        filterFields: {type: Object},
    },
    mixins: [analyticsMixins],
    data() {
        return {
            fetching: true,
            gridApi: null,
            itemsPerPage: 10,
            fetchedData: null,

            lastRequestTime: null,
        };
    },
    computed: {
        ...mapState(useMainStore, ['selectedLocale']),
        rowData() {
            if (this.fetchedData?.data) {
                return this.fetchedData.data.map(item => {
                    return {
                        vacancy: item.vacancy,
                        status: item.status,
                        creation_date: item.creation_date ?? null,
                        publish_date: item.publish_date ?? null,
                        depublish_date: item.depublish_date ?? null,
                        archived_date: item.archived_date ?? null,
                    }
                });
            }
        },
        columnDefs() {
            if (!this.rowData[0]) {
                return [];
            }
            return Object.keys(this.rowData[0]).map(key => {
                return this.getColumnDef(key);
            })
        },
        defaultColDef() {
            return {
                resizable: false,
                suppressMovable: true,
            }
        },
        totalPages() {
            return Math.ceil(this.fetchedData.data.length / this.itemsPerPage);
        }
    },
    methods: {
        async fetchData(fresh = true) {
            if (!this.sendRequest() && !fresh) {
                this.fetching = false;
                return;
            }
            this.fetching = true;
            return await AnalyticsService.vacancyDates(this.filterFields)
                .then(data => this.fetchedData = data)
                .then(() => this.fetching = false);
        },
        getColumnDef(field) {
            let columnDef = {};
            columnDef.field = field;
            columnDef.headerName = this.$t('analytics.vacancy_dates.' + field);
            if (field !== 'vacancy') {
                columnDef.flex = 1;
            } else {
                columnDef.tooltipField = field;
            }
            if (field.includes('date')) {
                columnDef.cellDataType = 'dateString';
                columnDef.comparator = (date1, date2) => {
                    return (date2 === null ? moment('1900-01-01') : moment(date2)).diff(
                        date1 === null ? moment('1900-01-01') : moment(date1)
                    );
                }
                columnDef.valueFormatter = (params) => {
                    return params.value ?? '-';
                }
            }
            if (field === 'status') {
                columnDef.cellRenderer = 'StatusComponent';
                columnDef.cellClass = 'flex items-center';
            }

            return columnDef;
        },
        onGridReady(params) {
            this.gridApi = params.api;
        },
        exportData() {
            this.gridApi.exportDataAsCsv()
        },
    },
}
</script>
<style>
.ag-theme-quartz {
    --ag-font-family: 'Montserrat', sans-serif;
}

.ag-header-container * {
    background-color: #008FFB !important;
}

.ag-header-cell * {
    color: white !important;
}
</style>
